import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, Order, OrderItem, PaymentMethod } from '@/types';
import { 
  Plus, X, ChevronLeft, ArrowLeft, Trash2, Check, Printer, 
  UsersRound, Edit, MessageSquare, Ban, AlertTriangle, CheckCircle,
  CloudUpload, CloudOff, RefreshCw, Percent, MinusCircle, RotateCw,
  CreditCard, FilePlus, CheckSquare, Loader2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import ProductSelector from '@/components/products/ProductSelector';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIsMobile } from '@/hooks/use-mobile';
import PaymentConfirmationDialog from '@/components/payment/PaymentConfirmationDialog';
import TableObservationsDialog from '@/components/tables/TableObservationsDialog';
import TableCancelDialog from '@/components/tables/TableCancelDialog';
import { InputNumber } from '@/components/ui/input-number';
import * as z from 'zod';
import { formatCurrency } from '@/lib/utils';
import { 
  isOrderModifiedSinceLastSync,
  calculateSubtotal,
  calculateDiscountValue,
  calculateOrderTotal,
  calculateTableTotal,
  getOrCreateTableExternalId
} from '@/services/orderApiService';

const tableSeatsSchema = z.object({
  seats: z.coerce.number().min(1, {
    message: "A mesa deve ter pelo menos 1 lugar",
  }).max(20, {
    message: "A mesa não pode ter mais de 20 lugares",
  }),
});

type TableSeatsFormValues = z.infer<typeof tableSeatsSchema>;

const cancelReasonSchema = z.object({
  reason: z.string().min(3, {
    message: "A justificativa deve ter pelo menos 3 caracteres",
  }),
});

type CancelReasonFormValues = z.infer<typeof cancelReasonSchema>;

const discountSchema = z.object({
  amount: z.coerce.number().min(0, {
    message: "O desconto não pode ser negativo",
  }),
  type: z.enum(['percentage', 'fixed']),
});

type DiscountFormValues = z.infer<typeof discountSchema>;

const TableDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { 
    tables, 
    activeTable, 
    setActiveTable, 
    createNewOrder, 
    completeOrder, 
    printOrder, 
    updateTableSeats,
    updateTableObservations,
    cancelTable,
    cancelOrderItem,
    synchronizeOrder,
    getSyncStatusLabel,
    getSyncStatusClass,
    updateItemQuantity,
    applyOrderDiscount,
    finalizeTable,
    refreshData
  } = useApp();
  
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [seatsDialogOpen, setSeatsDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [observationsDialogOpen, setObservationsDialogOpen] = useState(false);
  const [cancelTableDialogOpen, setCancelTableDialogOpen] = useState(false);
  const [itemToCancel, setItemToCancel] = useState<{orderId: string, itemId: string} | null>(null);
  const [cancelItemDialogOpen, setCancelItemDialogOpen] = useState(false);
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  
  const [finalizeTableDialogOpen, setFinalizeTableDialogOpen] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isClosingOrder, setIsClosingOrder] = useState(false);
  
  const activeOrder = activeTable?.orders?.find(o => o.id === activeOrderId) || null;
  const openOrders = activeTable?.orders?.filter(o => o.status === 'open') || [];
  const closedOrders = activeTable?.orders?.filter(o => o.status === 'paid') || [];
  
  const form = useForm<TableSeatsFormValues>({
    resolver: zodResolver(tableSeatsSchema),
    defaultValues: {
      seats: activeTable?.seats || 2,
    },
  });

  const cancelItemForm = useForm<CancelReasonFormValues>({
    resolver: zodResolver(cancelReasonSchema),
    defaultValues: {
      reason: "",
    },
  });
  
  const discountForm = useForm<DiscountFormValues>({
    resolver: zodResolver(discountSchema),
    defaultValues: {
      amount: activeOrder?.discount || 0,
      type: activeOrder?.discountType || 'percentage',
    },
  });
  
  useEffect(() => {
    if (activeTable) {
      form.setValue('seats', activeTable.seats);
    }
  }, [activeTable, form]);
  
  useEffect(() => {
    if (activeOrder) {
      discountForm.setValue('amount', activeOrder.discount || 0);
      discountForm.setValue('type', activeOrder.discountType || 'percentage');
    }
  }, [activeOrder, discountForm]);
  
  useEffect(() => {
    if (!id) return;
    
    const table = tables.find(t => t.id === id);
    if (table) {
      setActiveTable(table);
      
      const openOrders = table.orders.filter(o => o.status === 'open');
      if (openOrders.length > 0) {
        const sortedOrders = [...openOrders].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setActiveOrderId(sortedOrders[0].id);
      }
    } else {
      navigate('/tables');
    }
    
    return () => {
      setActiveTable(null);
    };
  }, [id, tables, setActiveTable, navigate, createNewOrder]);
  
  const handleSyncOrder = async () => {
    if (!activeOrderId) {
      toast({
        title: "Erro",
        description: "Não há pedido ativo para sincronizar",
        variant: "destructive"
      });
      return;
    }
    
    setIsSyncing(true);
    try {
      await synchronizeOrder(activeOrderId);
    } catch (error) {
      console.error("Erro ao sincronizar pedido:", error);
    } finally {
      setIsSyncing(false);
    }
  };
  
  if (!activeTable) {
    return <div className="text-center py-10">Carregando mesa...</div>;
  }
  
  const getTableStatusClass = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'occupied':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'reserved':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  const getTableStatus = (status: string) => {
    switch (status) {
      case 'available':
        return 'Disponível';
      case 'occupied':
        return 'Ocupada';
      case 'reserved':
        return 'Reservada';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  };
  
  const handleCreateOrder = () => {
    if (!activeTable) return;
    
    setIsCreatingOrder(true);
    
    try {
      getOrCreateTableExternalId(activeTable.id);
      
      const newOrder = createNewOrder(activeTable.id, 'dine-in');
      
      setActiveOrderId(newOrder.id);
      
      setProductDialogOpen(true);
      
      toast({
        title: "Novo pedido criado",
        description: `Pedido #${newOrder.id.substring(0, 8)} criado com sucesso`,
      });
    } catch (error) {
      console.error("Erro ao criar pedido:", error);
      toast({
        title: "Erro ao criar pedido",
        description: "Não foi possível criar um novo pedido",
        variant: "destructive"
      });
    } finally {
      setIsCreatingOrder(false);
    }
  };
  
  const handleCloseOrder = async (orderId: string) => {
    if (!orderId) return;
    
    setIsClosingOrder(true);
    
    try {
      const order = activeTable?.orders?.find(o => o.id === orderId);
      
      if (order && order.items.length > 0) {
        try {
          await printOrder(orderId);
          toast({
            title: "Pedido impresso",
            description: `O pedido #${order.id.substring(0, 8)} foi enviado para impressão.`,
          });
        } catch (printError) {
          console.error("Erro ao imprimir pedido:", printError);
          toast({
            title: "Erro na impressão",
            description: "Não foi possível imprimir o pedido, mas ele será fechado.",
            variant: "destructive"
          });
        }

        completeOrder(orderId);
        setActiveOrderId(null);
        refreshData();
        
        toast({
          title: "Pedido fechado",
          description: `O pedido #${order.id.substring(0, 8)} foi fechado. Você pode criar um novo pedido.`,
        });
      } else {
        toast({
          title: "Pedido vazio",
          description: "Não é possível fechar um pedido sem itens.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Erro ao fechar pedido:", error);
      toast({
        title: "Erro ao fechar pedido",
        description: "Não foi possível fechar o pedido",
        variant: "destructive"
      });
    } finally {
      setIsClosingOrder(false);
    }
  };
  
  const handleAddItems = () => {
    if (!activeOrderId) {
      handleCreateOrder();
    } else {
      setProductDialogOpen(true);
    }
  };
  
  const handleFinalizeTable = () => {
    const openOrders = activeTable?.orders.filter(o => o.status === 'open') || [];
    const hasAnyOrders = (activeTable?.orders.filter(o => o.status === 'open' || o.status === 'paid') || []).length > 0;
    
    if (hasAnyOrders) {
      const hasItems = activeTable?.orders.some(order => 
        (order.status === 'open' || order.status === 'paid') && order.items.length > 0
      );
      
      if (hasItems) {
        console.log("Abrindo diálogo de finalização da mesa");
        setFinalizeTableDialogOpen(true);
      } else {
        toast({
          title: "Nenhum item nos pedidos",
          description: "Adicione itens antes de finalizar a mesa.",
          variant: "destructive"
        });
      }
    } else {
      toast({
        title: "Nenhum pedido na mesa",
        description: "Crie um pedido antes de finalizar a mesa.",
        variant: "destructive"
      });
    }
  };
  
  const handleChangeItemQuantity = (orderId: string, itemId: string, quantity: number) => {
    if (updateItemQuantity) {
      updateItemQuantity(orderId, itemId, quantity);
    }
  };
  
  const handleApplyDiscount = (values: DiscountFormValues) => {
    if (!activeOrderId) return;
    
    if (applyOrderDiscount) {
      applyOrderDiscount(activeOrderId, values.amount, values.type);
      toast({
        title: "Desconto aplicado",
        description: `Desconto de ${values.type === 'percentage' ? `${values.amount}%` : formatCurrency(values.amount)} aplicado ao pedido`,
      });
    }
    
    setDiscountDialogOpen(false);
  };
  
  const handleConfirmFinalizeTable = async (
    order: Order | undefined, 
    paymentMethods: PaymentMethod[], 
    paymentValues: number[]
  ) => {
    if (!activeTable || !finalizeTable) return;
    
    try {
      setIsFinalizing(true);
      console.log('Finalizando mesa:', activeTable.id);
      console.log('Métodos de pagamento:', paymentMethods);
      console.log('Valores de pagamento:', paymentValues);
      
      const success = await finalizeTable(activeTable.id, paymentMethods, paymentValues);
      
      console.log('Resultado da finalização:', success);
      
      if (success) {
        toast({
          title: "Mesa finalizada",
          description: `Mesa ${activeTable.number} foi finalizada com sucesso.`,
        });
        
        setFinalizeTableDialogOpen(false);
        setActiveOrderId(null);
        
        setTimeout(() => {
          console.log('Redirecionando para /tables');
          navigate('/tables');
        }, 300);
      } else {
        toast({
          title: "Problema na finalização",
          description: "A mesa não pôde ser finalizada. Verifique os dados e tente novamente.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Erro ao finalizar mesa:", error);
      toast({
        title: "Erro ao finalizar mesa",
        description: "Ocorreu um erro ao tentar finalizar a mesa.",
        variant: "destructive"
      });
    } finally {
      setIsFinalizing(false);
    }
  };
  
  const handleConfirmPayment = async (
    order: Order | undefined,
    paymentMethods: PaymentMethod[], 
    paymentValues: number[]
  ) => {
    if (activeOrder && order && order.items.length > 0) {
      setIsPrinting(true);
      try {
        await printOrder(activeOrder.id);
      } catch (error) {
        console.error("Erro ao imprimir:", error);
      } finally {
        setIsPrinting(false);
      }
    }
    
    if (activeOrder) {
      completeOrder(activeOrder.id, paymentMethods[0]?.id);
      setActiveOrderId(null);
      
      toast({
        title: "Pedido finalizado",
        description: `O pedido foi finalizado com sucesso.`,
      });

      setPaymentDialogOpen(false);
    }
  };
  
  const handlePrintOrder = async (orderId: string) => {
    if (!orderId) return;
    
    setIsPrinting(true);
    try {
      await printOrder(orderId);
      toast({
        title: "Pedido impresso",
        description: `O pedido foi enviado para impressão.`,
      });
    } catch (error) {
      console.error("Erro ao imprimir:", error);
      toast({
        title: "Erro na impressão",
        description: "Não foi possível imprimir o pedido.",
        variant: "destructive"
      });
    } finally {
      setIsPrinting(false);
    }
  };
  
  const handleUpdateSeats = (values: TableSeatsFormValues) => {
    if (updateTableSeats && activeTable) {
      updateTableSeats(activeTable.id, values.seats);
      setSeatsDialogOpen(false);
      toast({
        title: "Lugares atualizados",
        description: `A mesa ${activeTable.number} agora tem ${values.seats} lugares`,
      });
    }
  };
  
  const handleSaveObservations = (observations: string) => {
    if (updateTableObservations && activeTable) {
      updateTableObservations(activeTable.id, observations);
      setObservationsDialogOpen(false);
      toast({
        title: "Observações salvas",
        description: "As observações da mesa foram atualizadas com sucesso",
      });
    }
  };
  
  const handleCancelTable = (reason: string) => {
    if (cancelTable && activeTable) {
      cancelTable(activeTable.id, reason);
      setCancelTableDialogOpen(false);
      toast({
        title: "Mesa cancelada",
        description: `A mesa ${activeTable.number} foi cancelada`,
      });
      navigate('/tables');
    }
  };

  const handleSelectOrder = (orderId: string) => {
    setActiveOrderId(orderId);
  };

  const handleOpenCancelItemDialog = (orderId: string, itemId: string) => {
    setItemToCancel({ orderId, itemId });
    setCancelItemDialogOpen(true);
  };

  const handleCancelItem = (values: CancelReasonFormValues) => {
    if (itemToCancel && cancelOrderItem) {
      cancelOrderItem(itemToCancel.orderId, itemToCancel.itemId, values.reason);
      cancelItemForm.reset();
      setCancelItemDialogOpen(false);
      setItemToCancel(null);
    }
  };
  
  const calculateTotalTableAmount = (): number => {
    if (!activeTable) return 0;
    
    const relevantOrders = activeTable.orders.filter(o => o.status === 'open' || o.status === 'paid');
    return calculateTableTotal(relevantOrders);
  };
  
  const getAllTableItems = (): { order: Order, items: OrderItem[] }[] => {
    if (!activeTable) return [];
    
    return activeTable.orders
      .filter(o => o.status === 'open' || o.status === 'paid')
      .map(order => ({
        order,
        items: order.items
      }))
      .filter(({ items }) => items.length > 0);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            className="mr-2" 
            onClick={() => navigate('/tables')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          
          <div className="flex flex-col">
            <h1 className="text-3xl font-bold tracking-tight">Mesa {activeTable.number}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-xs font-medium px-2 py-1 rounded-full border ${getTableStatusClass(activeTable.status)}`}>
                {getTableStatus(activeTable.status)}
              </span>
              
              <div className="flex items-center text-sm text-muted-foreground">
                <UsersRound className="h-3 w-3 mr-1" />
                <span>{activeTable.seats} lugares</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setSeatsDialogOpen(true)}
            className="h-8"
          >
            <Edit className="h-3 w-3 mr-1" />
            <span className="text-xs">Lugares</span>
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setObservationsDialogOpen(true)}
          >
            <MessageSquare className="h-3 w-3 mr-1" />
            <span className="text-xs">Observações</span>
          </Button>
          
          {activeTable.status !== 'cancelled' && (
            <Button 
              variant="danger" 
              size="sm"
              onClick={() => setCancelTableDialogOpen(true)}
              className="h-8"
            >
              <Ban className="h-3 w-3 mr-1" />
              <span className="text-xs">Cancelar</span>
            </Button>
          )}
        </div>
      </div>
      
      {activeTable.cancelReason && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800 mb-0.5">Mesa cancelada:</p>
                <p className="text-sm text-amber-700">{activeTable.cancelReason}</p>
                {activeTable.cancelDate && (
                  <p className="text-xs text-amber-600 mt-1">
                    Cancelada em: {new Date(activeTable.cancelDate).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {activeTable.observations && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-2">
              <MessageSquare className="h-4 w-4 text-yellow-600 mt-1 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800 mb-0.5">Observações:</p>
                <p className="text-sm text-yellow-700">{activeTable.observations}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {activeOrder && (
        <Card className={`${getSyncStatusClass(activeOrder.syncStatus)}`}>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-start gap-2">
                {activeOrder.syncStatus === 'synced' ? (
                  <CloudUpload className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                ) : activeOrder.syncStatus === 'failed' ? (
                  <CloudOff className="h-4 w-4 text-red-600 mt-1 flex-shrink-0" />
                ) : (
                  <RefreshCw className="h-4 w-4 text-orange-600 mt-1 flex-shrink-0" />
                )}
                <div>
                  <p className="text-sm font-medium mb-0.5">
                    Status: {getSyncStatusLabel(activeOrder.syncStatus)}
                  </p>
                  {activeOrder.lastSyncDate && (
                    <p className="text-xs mt-1">
                      Última sincronização: {new Date(activeOrder.lastSyncDate).toLocaleString('pt-BR')}
                    </p>
                  )}
                  {activeOrder.syncError && (
                    <p className="text-xs text-red-600 mt-1">
                      Erro: {activeOrder.syncError}
                    </p>
                  )}
                  {activeOrder.erpId && (
                    <p className="text-xs mt-1">
                      ID no ERP: {activeOrder.erpId}
                    </p>
                  )}
                </div>
              </div>
              
              <Button
                variant="outline"
                onClick={handleSyncOrder}
                disabled={isSyncing || !activeOrder || activeOrder.items.length === 0}
                className="h-8"
              >
                <RotateCw className={`h-3 w-3 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                <span className="text-xs">Sincronizar Pedido</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      <Dialog open={seatsDialogOpen} onOpenChange={setSeatsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Editar número de lugares</DialogTitle>
            <DialogDescription>
              Defina quantos lugares a mesa {activeTable.number} possui.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleUpdateSeats)} className="space-y-4 py-2">
              <FormField
                control={form.control}
                name="seats"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número de lugares</FormLabel>
                    <FormControl>
                      <InputNumber
                        min={1}
                        max={20}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSeatsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">Salvar alterações</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      
      <Dialog open={discountDialogOpen} onOpenChange={setDiscountDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Aplicar Desconto</DialogTitle>
            <DialogDescription>
              Defina o desconto a ser aplicado neste pedido.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...discountForm}>
            <form onSubmit={discountForm.handleSubmit(handleApplyDiscount)} className="space-y-4 py-2">
              <FormField
                control={discountForm.control}
                name="type"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel>Tipo de desconto</FormLabel>
                    <FormControl>
                      <div className="flex space-x-4">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="percentage"
                            className="mr-2"
                            checked={field.value === 'percentage'}
                            onChange={() => field.onChange('percentage')}
                          />
                          <label htmlFor="percentage">Percentual (%)</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="fixed"
                            className="mr-2"
                            checked={field.value === 'fixed'}
                            onChange={() => field.onChange('fixed')}
                          />
                          <label htmlFor="fixed">Valor fixo (R$)</label>
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={discountForm.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Valor do desconto {discountForm.watch('type') === 'percentage' ? '(%)' : '(R$)'}
                    </FormLabel>
                    <FormControl>
                      <InputNumber
                        min={0}
                        max={discountForm.watch('type') === 'percentage' ? 100 : 9999}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {activeOrder && (
                <div className="rounded-md bg-muted p-4 text-sm">
                  <div className="flex justify-between mb-2">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(calculateSubtotal(activeOrder))}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>Desconto:</span>
                    <span className="text-red-600">-{formatCurrency(
                      discountForm.watch('type') === 'percentage'
                        ? (calculateSubtotal(activeOrder) * discountForm.watch('amount')) / 100
                        : Math.min(discountForm.watch('amount'), calculateSubtotal(activeOrder))
                    )}</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span>Total:</span>
                    <span>{formatCurrency(
                      calculateOrderTotal(activeOrder)
                    )}</span>
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDiscountDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">Aplicar Desconto</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      
      <TableObservationsDialog
        tableId={activeTable.id}
        observations={activeTable.observations || ''}
        open={observationsDialogOpen}
        onOpenChange={setObservationsDialogOpen}
        onSave={handleSaveObservations}
      />
      
      <TableCancelDialog
        tableId={activeTable.id}
        open={cancelTableDialogOpen}
        onOpenChange={setCancelTableDialogOpen}
        onConfirm={handleCancelTable}
      />

      <Dialog open={cancelItemDialogOpen} onOpenChange={setCancelItemDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Cancelar item do pedido</DialogTitle>
            <DialogDescription>
              Informe o motivo do cancelamento deste item.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...cancelItemForm}>
            <form onSubmit={cancelItemForm.handleSubmit(handleCancelItem)} className="space-y-4 py-2">
              <FormField
                control={cancelItemForm.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Motivo do cancelamento</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Informe o motivo do cancelamento..." 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCancelItemDialogOpen(false)}>
                  Voltar
                </Button>
                <Button type="submit" variant="destructive">
                  Cancelar Item
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      
      {(activeTable?.status === 'available' || activeTable?.status === 'cancelled') ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-10">
              <h3 className="text-lg font-medium mb-2">
                {activeTable.status === 'cancelled' ? 'Mesa cancelada' : 'Mesa disponível'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {activeTable.status === 'cancelled' 
                  ? 'Esta mesa foi cancelada e não pode receber novos pedidos'
                  : 'Inicie um novo pedido para esta mesa'
                }
              </p>
              {activeTable.status !== 'cancelled' && (
                <Button 
                  onClick={handleCreateOrder} 
                  disabled={isCreatingOrder}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {isCreatingOrder ? "Criando..." : "Criar Pedido"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {activeOrder && (
            <Card className="border-2 border-primary bg-primary/5 shadow-md">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="flex items-center gap-2">
                    Pedido Ativo <Badge variant="secondary">Em andamento</Badge>
                  </CardTitle>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setProductDialogOpen(true)}
                    disabled={!activeOrderId}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {isMobile ? "Adicionar" : "Adicionar Item"}
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDiscountDialogOpen(true)}
                    disabled={!activeOrder || activeOrder.items.length === 0}
                  >
                    <Percent className="h-4 w-4 mr-2" />
                    {isMobile ? "" : "Desconto"}
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handlePrintOrder(activeOrder.id)}
                    disabled={isPrinting || activeOrder.items.length === 0}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    {isMobile ? "" : "Imprimir"}
                  </Button>
                  
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() => handleCloseOrder(activeOrder.id)}
                    disabled={isClosingOrder || activeOrder.items.length === 0}
                  >
                    {isClosingOrder ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckSquare className="h-4 w-4 mr-2" />
                    )}
                    {isClosingOrder ? "Fechando..." : "Fechar Pedido"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {activeOrder.items.length > 0 ? (
                  <div className="space-y-4">
                    <div className="border rounded-md divide-y">
                      {activeOrder.items.map(item => (
                        <div key={item.id} className="p-4 flex items-start md:items-center justify-between flex-wrap md:flex-nowrap">
                          <div className="flex items-center">
                            <div>
                              <p className="font-medium">{item.product?.name || 'Produto sem nome'}</p>
                              <div className="flex items-center text-sm text-muted-foreground">
                                <span className="mx-1">
                                  {item.product && typeof item.product.price === 'number' ? 
                                    formatCurrency(item.product.price) : 
                                    'Preço indisponível'}
                                </span>
                              </div>
                              {item.notes && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Obs: {item.notes}
                                </p>
                              )}
                              {item.cancelReason && (
                                <p className="text-xs text-red-500 mt-1">
                                  Cancelado: {item.cancelReason}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3 mt-2 md:mt-0 w-full md:w-auto justify-end">
                            {item.status !== 'cancelled' && (
                              <div className="flex items-center space-x-2">
                                <InputNumber
                                  compact
                                  value={item.quantity}
                                  onChange={(value) => handleChangeItemQuantity(activeOrder.id, item.id, value)}
                                  min={1}
                                  max={99}
                                />
                              </div>
                            )}
                            <Badge variant={
                              item.status === 'cancelled' ? 'destructive' :
                              item.status === 'delivered' ? 'default' :
                              item.status === 'ready' ? 'secondary' :
                              item.status === 'preparing' ? 'outline' : 
                              'outline'
                            }>
                              {item.status === 'pending' && 'Pendente'}
                              {item.status === 'preparing' && 'Preparando'}
                              {item.status === 'ready' && 'Pronto'}
                              {item.status === 'delivered' && 'Entregue'}
                              {item.status === 'cancelled' && 'Cancelado'}
                            </Badge>
                            <span className="font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-[100px] md:max-w-none">
                              {item.product && typeof item.product.price === 'number' && item.status !== 'cancelled' ? 
                                formatCurrency(item.product.price * item.quantity) : 
                                item.status === 'cancelled' ? 'Cancelado' : 'Preço indisponível'}
                            </span>
                            {item.status !== 'cancelled' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleOpenCancelItemDialog(activeOrder.id, item.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                                <span className="sr-only">Cancelar item</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="space-y-2 pt-4 border-t">
                      <div className="flex justify-between items-center">
                        <span>Subtotal</span>
                        <span>{formatCurrency(calculateSubtotal(activeOrder))}</span>
                      </div>
                      
                      {activeOrder.discount > 0 && (
                        <div className="flex justify-between items-center text-red-600">
                          <span>Desconto {activeOrder.discountType === 'percentage' && `(${activeOrder.discount}%)`}</span>
                          <span>-{formatCurrency(calculateDiscountValue(activeOrder))}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center font-bold pt-2 border-t">
                        <span>Total</span>
                        <span>{formatCurrency(calculateOrderTotal(activeOrder))}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-2">Nenhum item no pedido</p>
                    <Button variant="outline" size="sm" onClick={() => setProductDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Itens
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Resumo da Mesa</h3>
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleCreateOrder}
              disabled={isCreatingOrder}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <FilePlus className="h-4 w-4 mr-2" />
              {isCreatingOrder ? "Criando..." : "Criar Pedido"}
            </Button>
          </div>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle>
                Todos os Pedidos 
                <Badge variant="outline" className="ml-2">
                  {getAllTableItems().reduce((acc, { items }) => acc + items.filter(i => i.status !== 'cancelled').length, 0)} itens
                </Badge>
              </CardTitle>
              <Button 
                variant="default" 
                size="sm"
                onClick={handleFinalizeTable}
                disabled={calculateTotalTableAmount() <= 0 || isFinalizing}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {isFinalizing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Finalizando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Finalizar Mesa
                  </>
                )}
              </Button>
            </CardHeader>
            <CardContent>
              {getAllTableItems().length > 0 ? (
                <div className="space-y-4">
                  {getAllTableItems().map(({ order, items }, index) => (
                    <div 
                      key={order.id} 
                      className={`p-3 border rounded-md ${
                        order.id === activeOrderId 
                          ? 'border-primary border-2 bg-primary/10 shadow-sm' 
                          : order.status === 'paid'
                            ? 'bg-gray-100 border-gray-300'
                            : index % 2 === 0 
                              ? 'bg-gray-50'
                              : 'bg-white'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="font-medium flex items-center gap-2">
                          <span>Pedido #{order.id.substring(0, 8)}</span>
                          {order.id === activeOrderId && (
                            <Badge variant="secondary">Ativo</Badge>
                          )}
                          {order.status === 'paid' && (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Fechado</Badge>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-7 text-xs"
                            onClick={() => handlePrintOrder(order.id)}
                            disabled={isPrinting}
                          >
                            <Printer className="h-3.5 w-3.5 mr-1" />
                            Imprimir
                          </Button>
                          {order.status === 'open' && (
                            <Button 
                              variant={order.id === activeOrderId ? "default" : "ghost"} 
                              size="sm" 
                              className={`h-7 text-xs ${order.id === activeOrderId ? 'bg-primary/20 text-primary hover:bg-primary/30' : ''}`}
                              onClick={() => setActiveOrderId(order.id)}
                            >
                              <Edit className="h-3.5 w-3.5 mr-1" />
                              {order.id === activeOrderId ? "Editando" : "Editar"}
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        {items.filter(i => i.status !== 'cancelled').map(item => (
                          <div key={item.id} className="flex justify-between">
                            <span>
                              {item.quantity}x {item.product.name}
                            </span>
                            <span>
                              {formatCurrency(item.product.price * item.quantity)}
                            </span>
                          </div>
                        ))}
                        
                        {order.discount > 0 && (
                          <div className="flex justify-between text-red-600 pt-1 border-t">
                            <span>Desconto {order.discountType === 'percentage' && `(${order.discount}%)`}</span>
                            <span>-{formatCurrency(calculateDiscountValue(order))}</span>
                          </div>
                        )}
                        
                        <div className="flex justify-between font-medium pt-1 border-t">
                          <span>Total do Pedido</span>
                          <span>{formatCurrency(calculateOrderTotal(order))}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="pt-4 border-t">
                    <div className="flex justify-between items-center font-bold text-lg">
                      <span>Total da Mesa</span>
                      <span>{formatCurrency(calculateTotalTableAmount())}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-2">Nenhum pedido ativo na mesa</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleCreateOrder}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Pedido
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          
          <PaymentConfirmationDialog
            open={finalizeTableDialogOpen}
            onOpenChange={setFinalizeTableDialogOpen}
            onConfirm={handleConfirmFinalizeTable}
            isProcessing={isFinalizing}
            totalAmount={calculateTotalTableAmount()}
            title="Finalizar Mesa"
            description={`Finalize a mesa ${activeTable?.number} com um total de ${formatCurrency(calculateTotalTableAmount())}`}
          />
          
          <PaymentConfirmationDialog
            open={paymentDialogOpen}
            onOpenChange={setPaymentDialogOpen}
            onConfirm={handleConfirmPayment}
            isProcessing={isPrinting}
            totalAmount={activeOrder ? calculateOrderTotal(activeOrder) : 0}
            title="Finalizar Pedido"
            description={activeOrder ? `Finalize o pedido atual com um total de ${formatCurrency(calculateOrderTotal(activeOrder))}` : 'Finalize o pedido atual'}
          />
        </div>
      )}
      
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="sm:max-w-[600px] w-[96%] h-[80vh] max-h-[600px] overflow-auto">
          <DialogHeader>
            <DialogTitle>Adicionar Item ao Pedido</DialogTitle>
            <DialogDescription>
              Selecione os produtos para adicionar ao pedido
            </DialogDescription>
          </DialogHeader>
          
          <ProductSelector 
            orderId={activeOrderId || undefined} 
            onClose={() => setProductDialogOpen(false)}
          />
          
          <DialogFooter className="pt-2">
            <Button onClick={() => setProductDialogOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TableDetailPage;
